import type { AxiosError, AxiosResponse } from 'axios';
import { trackCvUploadComplete } from '../analytics/gtmEvents';
import type { CVViewModel, UserProfileViewModel } from '../../types/auth.types';
import type { UserSettingsResponse } from '../../types/userSettings.types';
import { API_METHODS } from './api.enums';
import newAPIService from './newAPIService';
import { apiAction } from './utils.actions';

export type UserSettingsPayload = {
    formatted_address?: string
    latitude?: number
    longitude?: number
    smartAgentNotificationType?: number
    showToEmployers?: boolean
    tags?: number[]
}
export type UserSettingsParams = { userid: number }
export type UserSettingsViewModel = {
    userid: number
}
export const getUserSettings = (
    params: UserSettingsParams,
    backdrop?: boolean,
) => apiAction<UserSettingsResponse>({
    url: `/userSettings/${params.userid}`,
    method: API_METHODS.GET,
    backdrop: backdrop ?? false,
});

export const postUserSettings = (
    params: UserSettingsParams, payload: UserSettingsPayload, backdrop,
) => apiAction<UserSettingsViewModel>({
    url: `/userSettings/${params.userid}`,
    method: API_METHODS.POST,
    payload,
    backdrop,
});

export const putUserSettings = (
    params: UserSettingsParams,
    payload: UserSettingsPayload,
    backdrop?: boolean,
) => apiAction<UserSettingsViewModel>({
    url: `/userSettings/${params.userid}`,
    method: API_METHODS.PUT,
    payload,
    backdrop: backdrop ?? false,
});

/**
 * @param {number} userid
 * @param {Object} data
 * @param {string} data.phone
 * @param {Object} options
 * @param {boolean} options.global
 */
export const userProfileApi = async (userid, data) => newAPIService.put(
    `/api/user-profile/${userid}`,
    data,
);

export type UserProfilePayload = {
    firstname?: string
    lastname?: string,
    email: string,
    phone: string
}
export type UserProfileParams = { userid: number }
export type UserApplyResponse = UserProfileViewModel
export const postUserProfile = (
    params: UserProfileParams, payload: UserProfilePayload, backdrop?: boolean,
) => apiAction<UserApplyResponse>({
    url: `/api/user-profile/${params.userid}`,
    method: API_METHODS.PUT,
    payload,
    backdrop,
});

export const getUserProfile = (
    params: UserProfileParams, backdrop?: boolean,
) => apiAction<UserApplyResponse>({
    url: `/api/user-profile/${params.userid}`,
    method: API_METHODS.GET,
    backdrop,
});

/**
 * upload user cv file
 * @param {number} userid
 * @param {Object} data
 * @param {Object} data.file form file using FormData and append('file', file)
 * e.g formData.append('file', document.querySelector('#my-file-el').files[0]);
 * @param {Object} options
 * @param {boolean} options.global
 */
type UploadUserCVParams = { userid: number };
type UploadUserCVPayload = FormData;
type UploadUserCVResponse = CVViewModel;
export const uploadUserCV = async (params: UploadUserCVParams, data: UploadUserCVPayload): Promise<AxiosResponse<UploadUserCVResponse>> => {
    try {
        const response = await newAPIService.put(
            `/userCV/${params.userid}`,
            data,
            { timeout: 2 * 60 * 1000 },
        );
        trackCvUploadComplete({ userid: params.userid, cvid: response.data.cvid });
        return response;
    } catch (error) {
        const err = error as AxiosError;

        console.error('err uploadUserCV: ', JSON.stringify(err));
        console.error(`toString: ${err.toString()}`);

        // Check if response exists before accessing it. like response is 0
        if (!err.response) {
            throw new Error('אירעה שגיאת רשת. אנא בדוק את החיבור שלך ונסה שוב.');
        }

        if (err.response.data === 'Internal Server Error') {
            const message = 'אירעה שגיאה והנתונים לא נשמרו. ניתן להעלות קורות חיים רק מסוג : txt, pdf, rtf, doc, docx או תמונה. גודל קובץ מקסימלי: 4MB';
            throw new Error(message);
        }

        throw new Error(err.response.data as string);
    }
};

type SetDefaultCVParams = { userid: number, cvid: number };
type SetDefaultCVPayload = { default: boolean };
type SetDefaultCVResponse = {
    cvid: number
};
export const setDefaultCV = async (
    params: SetDefaultCVParams, payload: SetDefaultCVPayload,
) => apiAction<SetDefaultCVResponse>({
    url: `/userCV/${params.userid}/cv/${params.cvid}`,
    method: API_METHODS.PUT,
    payload,
});

type DeleteCVParams = { userid: number, cvid: number };
export const deleteCV = async (
    params: DeleteCVParams,
) => apiAction<void>({
    url: `/userCV/${params.userid}/cv/${params.cvid}`,
    method: API_METHODS.DELETE,
});
